import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import { Attribute } from './attribute'
import { Entity } from './entity'
import { Id as _Id } from './id'
import { Identifiable as _Identifiable } from './identifiable'
import { Model } from './model'
import { Snapshot } from './snapshot'
import { Term } from './term'
import { Type } from './type'

// TODO: allow dimensions to extend other dimensions (e.g. gl-expenses and
// gl-budgets extend gl-accounts). This way you can have multiple entities with
// the same parent. E.g. many institutions will associate their expenses and
// their budgets with their research projects by associating a project with an
// account
export interface Dimension<TId extends Dimension.Id = Dimension.Id>
  extends Model<TId> {
  entities?: readonly Entity[]
  /**
   * These attributes are to be inheritted by entities in this dimension
   */
  attributes?: readonly Attribute[] // TODO: maybe rename to defaultAttributes
  snapshots?: readonly Snapshot[]
}
export namespace Dimension {
  export const isDimension: TypeChecker<Dimension> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.intersection(
        Model.getTypeChecker(Id.isId),
        TypeChecker.Factory.object<Difference<Dimension, Model<Id>>>({
          attributes: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Attribute.isAttribute),
          ),
          entities: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Entity.isEntity),
          ),
          snapshots: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Snapshot.isSnapshot),
          ),
        }),
      ),
  ).usingAlias('Dimension')
  /**
   * Just checks if the object's ID indicates that it is a dimension. This can
   * be used to distinguish between various model types without checking that
   * every field of the model is correct.
   */
  export const shouldBeDimension = TypeChecker.Factory.lazy(() =>
    Model.getLooseTypeChecker(Type.Dimension),
  )

  export interface Input
    extends Omit<
      Dimension,
      'id' | 'entities' | 'attributes' | 'terms' | 'snapshots'
    > {
    /**
     * Must be ordered from root entity to leaf entity
     */
    entities?: readonly Entity.Input.InContext[]
    /**
     * These attributes are to be inheritted by entities in this dimension
     */
    attributes?: readonly Attribute.Input.InContext[]
    partialId?: PartialId
    termIds?: readonly Term.Id[]
  }
  export namespace Input {
    export const isInput: TypeChecker<Input> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        TypeChecker.Factory.omit(isDimension, ['id', 'attributes']),
        TypeChecker.Factory.object<Difference<Input, Dimension>>({
          attributes: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Attribute.Input.InContext.isInContext),
          ),
          entities: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Entity.Input.InContext.isInContext),
          ),
          partialId: TypeChecker.Factory.optional(
            PartialId.Standard.isStandard,
          ),
          termIds: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Term.Id.isId),
          ),
        }),
      ),
    ).usingAlias('Dimension.Input')
  }

  export type Updates = Partial<
    Omit<Input, 'partialId' | 'attributes' | 'entities'>
  >
  export namespace Updates {
    export const isUpdates: TypeChecker<Updates> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.intersection(
          TypeChecker.Factory.omit(Input.isInput, [
            'partialId',
            'attributes',
            'entities',
          ]),
          TypeChecker.Factory.object<Difference<Updates, Input>>({
            name: TypeChecker.Factory.optional(TypeChecker.isString),
          }),
        ),
    ).usingAlias('Dimension.Updates')
  }
  // TODO: implement typechecker

  export type Identifiable<TPartialId extends PartialId = PartialId> =
    | _Identifiable<Type.Dimension, TPartialId, Id.Context>
    | Omit<
        _Id.Parts<Type.Dimension, TPartialId, Id.Context>,
        'type' | 'context'
      >
    | Entity.Identifiable<PartialId, TPartialId>
    | Snapshot.Identifiable<PartialId, TPartialId>
    | Attribute.Identifiable<
        PartialId,
        _Id.UsingSeparator.On<Id<TPartialId>, _Id.Separator.WithinSection>
      >
  export namespace Identifiable {
    export const isIdentifiable: TypeChecker<Identifiable> =
      TypeChecker.Factory.lazy(() =>
        _Identifiable.getTypeChecker(Type.Dimension, Id.Context.isContext),
      ).usingAlias('Dimension.Identifiable')
  }

  export type Id<TPartialId extends PartialId = PartialId> = _Id<
    Type.Dimension,
    TPartialId,
    Id.Context
  >
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
      _Id.getTypeChecker(Type.Dimension, Id.Context.isContext),
    ).usingAlias('Dimension.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    export type Of<TIdentifiable extends Identifiable> =
      TIdentifiable extends Identifiable<infer TPartialId>
        ? Id<TPartialId>
        : never

    /**
     * Retrieves the {@link Dimension.Id} of the given
     * {@link Dimension.Identifiable}
     *
     * __NOTE:__ because both entities and dimensions can have attributes, when
     * given an attribute, we only return the dimension ID for dimension
     * attributes. Entity attributes produce `undefined` so that we can
     * distinguish between the two. If you want the dimension ID for an entity
     * attribute, use `Dimension.Id.from(Entity.Id.from(attribute))`
     */
    export function from<TIdentifiable extends Identifiable>(
      source: TIdentifiable,
    ): Of<TIdentifiable>
    export function from(
      source:
        | Identifiable
        // These may or may not be associated with a Dimension
        | Snapshot.Identifiable
        | Attribute.Identifiable
        | Nullish,
    ): Id | undefined
    export function from(
      source:
        | Identifiable
        | Snapshot.Identifiable
        | Attribute.Identifiable
        | Nullish,
    ): Id | undefined {
      if (!source) {
        return undefined
      }
      if (Entity.Identifiable.isIdentifiable(source)) {
        return _Id.from({
          context: _Id.Context.NONE,
          partialId: Entity.Id.Context.from(source),
          type: Type.Dimension,
        })
      }
      if (Snapshot.Identifiable.isIdentifiable(source)) {
        const snapshotIdContext = Snapshot.Id.Context.from(source)
        if (snapshotIdContext === _Id.Context.NONE) {
          return undefined
        }
        return _Id.from({
          context: _Id.Context.NONE,
          partialId: snapshotIdContext,
          type: Type.Dimension,
        })
      }
      if (Attribute.Identifiable.isIdentifiable(source)) {
        const context = Attribute.Id.Context.from(source)
        if (Attribute.Id.Context.ForAll.isForAll(context)) {
          return undefined
        }
        const hostId = _Id.Separator.swap(
          context,
          _Id.Separator.WithinSection,
          _Id.Separator.BetweenSections,
        )
        return Dimension.Id.isId(hostId) ? hostId : undefined
      }
      return _Id.from<Type.Dimension, PartialId, Context>(
        _Identifiable.ForSpecificType.bind(source, Type.Dimension),
      )
    }

    export function isSame(...identifiables: readonly Identifiable[]): boolean {
      if (!identifiables.length) {
        return true
      }
      const firstDimensionId = Dimension.Id.from(identifiables[0])
      return identifiables
        .slice(1)
        .every(
          identifiable => Dimension.Id.from(identifiable) === firstDimensionId,
        )
    }

    export type Context = _Id.Context.None
    export namespace Context {
      export const isContext: TypeChecker<Context> =
        _Id.Context.None.isNone.usingAlias('Dimension.Id.Context')
    }
  }
}
